import React, { useRef, useState, useEffect } from 'react';
import { motion, useAnimationFrame, useMotionValue } from 'framer-motion';
import { Phone } from './ui/Phone';
import { generateVideoThumbnail } from '../lib/utils';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { CDN_URL } from '../constants';

export const WorkCarousel: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [singleSetWidth, setSingleSetWidth] = useState(0);
  
  // Utilizziamo un MotionValue per gestire la posizione X in modo performante
  // Iniziamo a 0, ma verrà aggiornato non appena misuriamo la larghezza
  const x = useMotionValue(0);

  const cases = [
    // Social Proof / High Numbers
    { name: "Pier 500k TikTok", videoUrl: `${CDN_URL}/Progetti/Pier%20500k%20tiktok.mp4` },
    { name: "780K Insta", videoUrl: `${CDN_URL}/Progetti/780K%20INSTA.mp4` },
    { name: "750K Insta", videoUrl: `${CDN_URL}/Progetti/750K%20INSTA.mp4` },
    { name: "360K Insta", videoUrl: `${CDN_URL}/Progetti/360K%20INSTA.mp4` },
    { name: "70K TikTok", videoUrl: `${CDN_URL}/Progetti/70%20K%20TIKTOK.mp4` },
    { name: "40K Insta", videoUrl: `${CDN_URL}/Progetti/40K%20INSTA.mp4` },
    
    // Clienti & Progetti
    { name: "Andrea Sanseverinati", videoUrl: `${CDN_URL}/Progetti/andrea%20Sanseverinati%20arredamenti.mp4` },
    { name: "Bontempi Camerano", videoUrl: `${CDN_URL}/Progetti/Bontempi%20Camerano.mp4` },
    { name: "Brillantini Gioielli", videoUrl: `${CDN_URL}/Progetti/BRILLANTINI%20gioielli.mp4` },
    { name: "Casa Gourmet", videoUrl: `${CDN_URL}/Progetti/Casa%20Gourmet.mp4` },
    { name: "Curry e Burger", videoUrl: `${CDN_URL}/Progetti/CURRY%20E%20BURGER_10.mp4` },
    { name: "Di Gusto", videoUrl: `${CDN_URL}/Progetti/DIGusto.mp4` },
    { name: "Dott. Federico Ascenzi", videoUrl: `${CDN_URL}/Progetti/Dottor%20Federico%20Ascenzi.mp4` },
    { name: "Dott.ssa Federica Ranauro", videoUrl: `${CDN_URL}/Progetti/Dottoressa%20Federica%20Ranauro.mp4` },
    { name: "Helios", videoUrl: `${CDN_URL}/Progetti/HELIOS%20(2).mp4` },
    { name: "Il Cuoco e il Pastaio", videoUrl: `${CDN_URL}/Progetti/il%20cuoco%20e%20il%20pastaio.mp4` },
    { name: "La Rotonda", videoUrl: `${CDN_URL}/Progetti/La%20rotonda.mp4` },
    { name: "Millevoglie", videoUrl: `${CDN_URL}/Progetti/MILLEVOGLIE%20PANETTONI%20CORRETT.mp4` },
    { name: "Zetasport", videoUrl: `${CDN_URL}/Progetti/zetasport.mp4` },
  ];

  // Quadruplichiamo la lista per creare un buffer infinito robusto
  // Set 1 (Buffer Sinistro) | Set 2 (Attivo) | Set 3 (Attivo) | Set 4 (Buffer Destro)
  const infiniteCases = [...cases, ...cases, ...cases, ...cases];

  useEffect(() => {
    const calculateWidth = () => {
      if (containerRef.current) {
        const totalScrollWidth = containerRef.current.scrollWidth;
        // La larghezza di un singolo set è il totale diviso 4
        const setWidth = totalScrollWidth / 4;
        setSingleSetWidth(setWidth);
        
        // Posizioniamo inizialmente il carosello al secondo set (offset negativo)
        // per permettere lo scroll in entrambe le direzioni senza vedere il bordo
        if (x.get() === 0) {
            x.set(-setWidth);
        }
      }
    };

    // Calcolo iniziale
    // Piccolo timeout per assicurarsi che il render sia completo
    const timer = setTimeout(calculateWidth, 100);
    
    window.addEventListener('resize', calculateWidth);
    return () => {
        window.removeEventListener('resize', calculateWidth);
        clearTimeout(timer);
    };
  }, []);

  // Loop di animazione fluido
  useAnimationFrame((t, delta) => {
    if (singleSetWidth === 0) return;

    // Se l'utente non sta trascinando, muoviamo il carosello automaticamente
    if (!isDragging) {
        // Velocità di scorrimento (pixel per frame normalizzati)
        // -0.5 è una velocità lenta e piacevole
        const moveBy = -0.5 * (delta / 16.667); 
        let newX = x.get() + moveBy;
        
        x.set(newX);
    }

    // LOGICA DI LOOP INFINITO
    // Controlliamo i confini per "teletrasportare" il carosello e creare l'illusione infinita.
    // Lo facciamo indipendentemente se stiamo trascinando o no, per evitare blocchi.
    
    const currentX = x.get();
    
    // Se siamo andati troppo a sinistra (oltre il 3° set), saltiamo indietro al 2° set
    if (currentX <= -(singleSetWidth * 3)) {
        x.set(currentX + singleSetWidth);
    }
    // Se siamo andati troppo a destra (dentro il 1° set), saltiamo avanti al 2° set
    else if (currentX > -singleSetWidth) {
        x.set(currentX - singleSetWidth);
    }
  });

  return (
    <section className="py-24 bg-black relative select-none overflow-hidden border-b border-white/5">
      
      {/* Ambient Light Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-[800px] h-[500px] bg-white/[0.08] blur-[150px] rounded-full pointer-events-none z-0" />

      {/* Header / Instructions */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 mb-8 flex justify-between items-end">
        <div></div>
        
        {/* Interaction Hint */}
        <div className="hidden md:flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-widest font-medium animate-pulse">
            <ArrowLeft size={14} />
            <span>Drag or Watch</span>
            <ArrowRight size={14} />
        </div>
      </div>

      {/* 
        Interactive Carousel Container
      */}
      <div className="relative z-10 pl-0">
        <motion.div 
            ref={containerRef}
            className="flex gap-12 md:gap-24 py-12 px-6 md:px-20 w-max cursor-grab active:cursor-grabbing"
            style={{ x }}
            drag="x"
            onDragStart={() => setIsDragging(true)}
            onDragEnd={() => setIsDragging(false)}
            // Nessun vincolo (dragConstraints) per permettere lo scroll infinito
        >
            {infiniteCases.map((item, idx) => (
                <motion.div 
                    key={idx} 
                    className="w-[200px] md:w-[320px] shrink-0 flex flex-col relative"
                >
                    <div className="mb-8 transform transition-transform hover:scale-105 duration-500">
                        <Phone 
                            videoUrl={item.videoUrl}
                            image={generateVideoThumbnail(item.videoUrl, 400)}
                            loading="lazy"
                            className="!border-[2px] md:!border-[3px] border-white/30 shadow-[0_0_40px_rgba(255,255,255,0.1)] rounded-[2rem] md:rounded-[2.5rem] pointer-events-none" 
                        />
                    </div>

                    <div className="text-center px-1 mt-6 relative z-20 bg-transparent">
                        <h3 className="text-xl md:text-3xl font-bold text-white mb-2 uppercase tracking-tighter font-['Space_Grotesk'] leading-tight">
                            {item.name}
                        </h3>
                    </div>
                </motion.div>
            ))}
        </motion.div>
      </div>
      
    </section>
  );
};